import { useCallback, useContext } from "react";
import { RBACContext } from "./context";
import type { AdminRoleName } from "./types";
import type { PermissionModule } from "./modules";

export function useRBAC() {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error("useRBAC must be used within an RBACProvider");
  }
  return context;
}

export function useHasPermission(codename: string): boolean {
  const { hasPermission } = useRBAC();
  return hasPermission(codename);
}

export function useHasRole(roleName: AdminRoleName): boolean {
  const { hasRole } = useRBAC();
  return hasRole(roleName);
}

/**
 * Typed permission checks for one module.
 *
 *     const can = useCan(FOOD);
 *     <Button disabled={!can("restaurant", "approve")}>Approve</Button>
 *
 * The resource/action pair is checked at COMPILE time by `defineModule`, so a
 * typo fails `tsc` instead of silently returning false and hiding a control
 * nobody can find. Backed by the same exact-match `hasPermission` as everything
 * else -- no wildcards, no implication.
 */
export function useCan<T extends Record<string, readonly string[]>>(
  module: PermissionModule<T>,
) {
  const { hasPermission } = useRBAC();
  return useCallback(
    <R extends keyof T & string>(resource: R, action: T[R][number]) =>
      hasPermission(module.can(resource, action)),
    [hasPermission, module],
  );
}

/** True if the user holds ANY of the given codenames. */
export function useHasAnyPermission(codenames: string[]): boolean {
  const { hasPermission } = useRBAC();
  return codenames.some((c) => hasPermission(c));
}
